// useQueueVoice — v2 voice announcements for "now serving" patients.
//
// Self-contained re-implementation (no v1 hook): given the doctor's current
// ongoing patients, it announces each newly-ongoing one once via the backend
// TTS, playing the clip twice (clearer in a noisy waiting room). Browsers
// block autoplay audio until a user gesture, so the first announcement raises
// `permissionNeeded`; calling `allow()` (from a click) unlocks and drains.
//
// Only a real autoplay refusal (NotAllowedError) re-raises the prompt; any
// other playback failure drops that announcement, otherwise one bad clip
// re-opens the prompt forever. A single audio element is reused for every
// clip and primed with silence inside the allow() click, so the TV browser
// keeps treating it as user-unlocked even though the clip arrives later.
import { useCallback, useEffect, useRef, useState } from "react";
import { synthesizeSpeech } from "../api/speech";
import { setAdsDucked } from "../utils/adAudio";

export interface VoiceAnnouncement {
  bookingId: string;
  doctorName: string; // each announcement carries its own doctor
  patientName: string;
  // Fresh per transfer cycle, so the same booking re-announces if transferred
  // away and back. Falsy = no cycle.
  transferCycleId?: string | null;
  room?: string;
}

const PERMISSION_KEY = "audioPermission";
const hasPermission = () =>
  localStorage.getItem(PERMISSION_KEY) === "allowed";
const setPermission = (ok: boolean) =>
  localStorage.setItem(PERMISSION_KEY, ok ? "allowed" : "denied");

const dedupKey = (a: VoiceAnnouncement) =>
  `${a.bookingId}::${a.transferCycleId ?? ""}`;

const isAutoplayBlocked = (err: unknown) => {
  const e = err as DOMException | undefined;
  return (
    e?.name === "NotAllowedError" ||
    !!e?.message?.includes("user gesture") ||
    !!e?.message?.includes("interact with the document")
  );
};

// 0.1s of 8kHz mono silence as a WAV data URI — played inside the allow()
// click to unlock the shared audio element.
const SILENT_WAV = (() => {
  const samples = 800;
  const buf = new Uint8Array(44 + samples);
  const dv = new DataView(buf.buffer);
  const str = (o: number, t: string) =>
    t.split("").forEach((c, i) => dv.setUint8(o + i, c.charCodeAt(0)));
  str(0, "RIFF");
  dv.setUint32(4, 36 + samples, true);
  str(8, "WAVE");
  str(12, "fmt ");
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true); // PCM
  dv.setUint16(22, 1, true); // mono
  dv.setUint32(24, 8000, true);
  dv.setUint32(28, 8000, true);
  dv.setUint16(32, 1, true);
  dv.setUint16(34, 8, true);
  str(36, "data");
  dv.setUint32(40, samples, true);
  buf.fill(128, 44); // 8-bit PCM silence
  let bin = "";
  buf.forEach((b) => (bin += String.fromCharCode(b)));
  return `data:audio/wav;base64,${btoa(bin)}`;
})();


// ONE instance for the whole board: all doctors' announcements share a single
// queue and play strictly one after another (no overlap on first load).
export function useQueueVoice(
  language: string,
  ongoing: VoiceAnnouncement[]
) {
  const announced = useRef<Set<string>>(new Set());
  const queue = useRef<VoiceAnnouncement[]>([]);
  const playing = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [permissionNeeded, setPermissionNeeded] = useState(false);

  const playOnce = useCallback(
    (b64: string) =>
      new Promise<void>((resolve, reject) => {
        const audio = (audioRef.current ??= new Audio());
        audio.onended = () => resolve();
        audio.onerror = () => reject(new Error("audio playback error"));
        audio.src = `data:audio/mp3;base64,${b64}`;
        audio.play().catch(reject);
      }),
    []
  );

  const drain = useCallback(async () => {
    if (playing.current) return;
    playing.current = true;
    try {
      while (queue.current.length > 0) {
        const next = queue.current[0];
        const key = dedupKey(next);

        if (announced.current.has(key)) {
          queue.current.shift();
          continue;
        }
        if (!next.doctorName || !next.patientName) {
          announced.current.add(key);
          queue.current.shift();
          continue;
        }
        if (!hasPermission()) {
          setPermissionNeeded(true); // keep item queued; allow() will resume
          return;
        }

        let b64: string | null = null;
        try {
          b64 = await synthesizeSpeech({
            language,
            doctor: next.doctorName,
            patient: next.patientName,
            room: next.room,
          });
        } catch (err) {
          console.warn("[queue-v2] TTS request failed:", err);
          announced.current.add(key); // don't get stuck on a bad item
          queue.current.shift();
          continue;
        }
        if (!b64) {
          announced.current.add(key);
          queue.current.shift();
          continue;
        }

        try {
          setAdsDucked(true);
          await playOnce(b64);
          await playOnce(b64); // play twice
          announced.current.add(key);
          queue.current.shift();
        } catch (err) {
          if (isAutoplayBlocked(err)) {
            // Autoplay blocked → ask for permission, keep the item queued.
            setPermissionNeeded(true);
            return;
          }
          // Decode/network/interrupted playback: skip it, don't re-prompt.
          console.warn("[queue-v2] announcement playback failed:", err);
          announced.current.add(key);
          queue.current.shift();
        } finally {
          setAdsDucked(false);
        }
      }
    } finally {
      playing.current = false;
    }
  }, [language, playOnce]);

  // Enqueue freshly-ongoing patients whenever the ongoing set changes.
  const signature = ongoing.map(dedupKey).join("|");
  useEffect(() => {
    const fresh = ongoing.filter(
      (o) =>
        !announced.current.has(dedupKey(o)) &&
        !queue.current.some((q) => dedupKey(q) === dedupKey(o))
    );
    if (fresh.length) {
      queue.current.push(...fresh);
      drain();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, language]);

  const allow = useCallback(() => {
    // Must run synchronously in the click/keypress handler: unlock the shared
    // element now, before the async TTS fetch eats the user gesture.
    const audio = (audioRef.current ??= new Audio());
    audio.onended = null;
    audio.onerror = null;
    audio.src = SILENT_WAV;
    audio.play().catch(() => {});
    setPermission(true);
    setPermissionNeeded(false);
    drain();
  }, [drain]);

  const deny = useCallback(() => {
    setPermission(false);
    setPermissionNeeded(false);
    queue.current = [];
  }, []);

  useEffect(
    () => () => {
      if (audioRef.current) audioRef.current.pause();
      setAdsDucked(false);
    },
    []
  );

  return { permissionNeeded, allow, deny };
}
